import React, { useState } from 'react';

const CompanyIdMappings = ({
  companyIdMappings,
  loading,
  fetchCompanyIdMappings,
  handleSaveCompanyIdMapping,
  handleDeleteCompanyIdMapping
}) => {
  const [showModal, setShowModal] = useState(false);
  const [editingMapping, setEditingMapping] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    hubspot_id: '',
    autotask_id: '',
    company_name: '',
    is_active: true
  });

  const filteredMappings = companyIdMappings.filter(m =>
    m.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    m.hubspot_id?.includes(searchTerm) ||
    m.autotask_id?.includes(searchTerm)
  );

  const handleCreate = () => {
    setEditingMapping(null);
    setFormData({
      hubspot_id: '',
      autotask_id: '',
      company_name: '',
      is_active: true
    });
    setShowModal(true);
  };

  const handleEdit = (mapping) => {
    setEditingMapping(mapping);
    setFormData({
      hubspot_id: mapping.hubspot_id,
      autotask_id: mapping.autotask_id,
      company_name: mapping.company_name || '',
      is_active: mapping.is_active
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const success = await handleSaveCompanyIdMapping(formData, editingMapping?.id);
    if (success) setShowModal(false);
  };

  return (
    <div className="page-content">
      <div className="page-header">
        <div className="header-left">
          <h2>Company ID Mappings</h2>
          <p>Map HubSpot Company IDs to Autotask Company IDs</p>
        </div>
        <div className="header-actions">
          <input
            type="text"
            placeholder="🔍 Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <button onClick={fetchCompanyIdMappings} className="btn btn-secondary" disabled={loading}>
            {loading ? '⏳' : '🔄'} Refresh
          </button>
          <button onClick={handleCreate} className="btn btn-primary">
            ➕ Add Mapping
          </button>
        </div>
      </div>

      <div className="stats-row">
        <div className="stat-card">
          <span className="stat-value">{companyIdMappings.length}</span>
          <span className="stat-label">Total Mappings</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">{companyIdMappings.filter(m => m.is_active).length}</span>
          <span className="stat-label">Active</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">{companyIdMappings.filter(m => !m.is_active).length}</span>
          <span className="stat-label">Inactive</span>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading company mappings...</div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Status</th>
                <th>Company Name</th>
                <th>HubSpot ID</th>
                <th></th>
                <th>Autotask ID</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredMappings.length === 0 ? (
                <tr>
                  <td colSpan="6" className="no-data">No company mappings found</td>
                </tr>
              ) : (
                filteredMappings.map((mapping) => (
                  <tr key={mapping.id} className={!mapping.is_active ? 'inactive' : ''}>
                    <td>
                      <span className={`status-badge ${mapping.is_active ? 'active' : 'inactive'}`}>
                        {mapping.is_active ? '✅' : '❌'}
                      </span>
                    </td>
                    <td>
                      <strong>{mapping.company_name || '—'}</strong>
                    </td>
                    <td>
                      <code>{mapping.hubspot_id}</code>
                    </td>
                    <td className="arrow-cell">↔</td>
                    <td>
                      <code>{mapping.autotask_id}</code>
                    </td>
                    <td>
                      <button onClick={() => handleEdit(mapping)} className="btn btn-sm btn-edit">
                        ✏️
                      </button>
                      <button onClick={() => handleDeleteCompanyIdMapping(mapping.id)} className="btn btn-sm btn-delete">
                        🗑️
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>{editingMapping ? 'Edit Company Mapping' : 'Create Company Mapping'}</h3>
              <button onClick={() => setShowModal(false)} className="modal-close">×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Company Name</label>
                  <input
                    type="text"
                    value={formData.company_name}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    placeholder="e.g., Acme Corp"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>HubSpot ID</label>
                    <input
                      type="text"
                      value={formData.hubspot_id}
                      onChange={(e) => setFormData({ ...formData, hubspot_id: e.target.value })}
                      placeholder="e.g., 202643517916"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Autotask ID</label>
                    <input
                      type="text"
                      value={formData.autotask_id}
                      onChange={(e) => setFormData({ ...formData, autotask_id: e.target.value })}
                      placeholder="e.g., 29682887"
                      required
                    />
                  </div>
                </div>

                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                  Active
                </label>
              </div>
              <div className="modal-footer">
                <button type="button" onClick={() => setShowModal(false)} className="btn btn-secondary">
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingMapping ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyIdMappings;